import { useState, useEffect } from 'react';
import { TrendingUp, Users, Mail, MousePointer, BarChart3, ArrowRight, Calendar, Target, Activity, Zap } from 'lucide-react';
import ReactECharts from 'echarts-for-react';
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";

const Dashboard = () => {
  const getGreeting = () => {
    const currentHour = new Date().getHours();
    
    if (currentHour < 12) {
      return "Good Morning";
    } else if (currentHour < 18) {
      return "Good Afternoon";
    } else {
      return "Good Evening";
    }
  };

  const greeting = useState(getGreeting())[0];
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dash, setDash] = useState({
    metrics: {
      totalFans: 0,
      newFans30d: 0,
      totalClicks: 0,
      conversionRate: 0,
      campaignsSent: 0,
      messagesSent: 0,
      totalGuests: 0,
    },
    charts: {
      fanGrowth: { labels: ['Jan','Feb','Mar','Apr','May','Jun'], series: [0,0,0,0,0,0] },
      campaign: { labels: [], values: [] }
    },
    topLinks: []
  });

  // Build and download a CSV containing dashboard metrics, charts, and top links
  const exportDashboardCsv = () => {
    try {
      const lines = []

      const esc = (v) => {
        if (v === null || v === undefined) return ''
        const s = String(v)
        return '"' + s.replace(/"/g, '""') + '"'
      }

      // Metrics section
      lines.push('Metrics')
      lines.push('Key,Value')
      const m = dash.metrics || {}
      const metricEntries = [
        ['Total Fans', m.totalFans],
        ['Total Guests', m.totalGuests],
        ['New Fans (30d)', m.newFans30d],
        ['Total Clicks', m.totalClicks],
        ['Conversion Rate (%)', typeof m.conversionRate === 'number' ? m.conversionRate.toFixed(2) : m.conversionRate],
        ['Campaigns Sent', m.campaignsSent],
        ['Messages Sent', m.messagesSent],
      ]
      metricEntries.forEach(([k, v]) => lines.push(`${esc(k)},${esc(v)}`))
      lines.push('')

      // Fan Growth chart section
      const fg = dash.charts?.fanGrowth || { labels: [], series: [] }
      lines.push('Fan Growth')
      lines.push('Month,Fans')
      const fgLabels = Array.isArray(fg.labels) ? fg.labels : []
      const fgSeries = Array.isArray(fg.series) ? fg.series : []
      const fgLen = Math.max(fgLabels.length, fgSeries.length)
      for (let i = 0; i < fgLen; i++) {
        const month = fgLabels[i] ?? ''
        const val = fgSeries[i] ?? ''
        lines.push(`${esc(month)},${esc(val)}`)
      }
      lines.push('')

      // Campaign chart section
      const camp = dash.charts?.campaign || { labels: [], values: [] }
      lines.push('Campaign Performance')
      lines.push('Label,Value')
      const cLabels = Array.isArray(camp.labels) ? camp.labels : []
      const cValues = Array.isArray(camp.values) ? camp.values : []
      const cLen = Math.max(cLabels.length, cValues.length)
      for (let i = 0; i < cLen; i++) {
        const label = cLabels[i] ?? ''
        const val = cValues[i] ?? ''
        lines.push(`${esc(label)},${esc(val)}`)
      }
      lines.push('')

      // Top Links section
      lines.push('Top Links')
      lines.push('Rank,Name,Clicks,Percentage')
      const top = Array.isArray(dash.topLinks) ? dash.topLinks : []
      top.forEach((t, idx) => {
        const rank = idx + 1
        const name = t?.name ?? ''
        const clicks = t?.clicks ?? 0
        const pct = t?.percentage ?? 0
        lines.push(`${esc(rank)},${esc(name)},${esc(clicks)},${esc(pct)}`)
      })

      const csv = lines.join('\n')
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      const date = new Date().toISOString().slice(0, 10)
      a.href = url
      a.download = `dashboard_export_${date}.csv`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (e) {
      console.warn('CSV export failed:', e?.message || e)
    }
  }

  // Fetch user profile, subscription, and plan
  useEffect(() => {
    if (!user) return;
    Promise.all([
      supabase.from('profiles').select('full_name, email, role').eq('id', user.id).maybeSingle(),
      supabase.from('subscriptions').select('plan_id, status, session_id, customer_id').eq('user_id', user.id).maybeSingle()
    ]).then(async ([{ data: profileData }]) => {
      setProfile(profileData);
    });
  }, [user]);

  // Load dashboard data
  useEffect(() => {
    const load = async () => {
      if (!user?.id) return;
      try {
        setLoading(true);
  const res = await fetch(`https://backend.amply.buildfast.us/dashboard/${user.id}`);
        const data = await res.json();
        if (res.ok && data?.success) {
          setDash(data);
        }
      } catch (e) {
        console.warn('Dashboard fetch failed:', e?.message || e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user?.id]);

  // Chart configuration options
  const fanGrowthOption = {
    tooltip: {
      trigger: 'axis',
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      borderColor: '#e2e8f0',
      textStyle: { color: '#334155' }
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '3%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: dash.charts.fanGrowth.labels,
      axisLine: { lineStyle: { color: '#e2e8f0' } },
      axisTick: { lineStyle: { color: '#e2e8f0' } },
      axisLabel: { color: '#64748b' }
    },
    yAxis: {
      type: 'value',
      axisLine: { lineStyle: { color: '#e2e8f0' } },
      axisTick: { lineStyle: { color: '#e2e8f0' } },
      axisLabel: { color: '#64748b' },
      splitLine: { lineStyle: { color: '#f1f5f9' } }
    },
    series: [{
      data: dash.charts.fanGrowth.series,
      type: 'line',
      smooth: true,
      lineStyle: { color: '#3b82f6', width: 3 },
      itemStyle: { color: '#3b82f6' },
      areaStyle: {
        color: {
          type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
          colorStops: [
            { offset: 0, color: 'rgba(59, 130, 246, 0.2)' },
            { offset: 1, color: 'rgba(59, 130, 246, 0.05)' }
          ]
        }
      }
    }]
  };

  const campaignOption = {
    tooltip: {
      trigger: 'axis',
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      borderColor: '#e2e8f0'
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '3%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: dash.charts.campaign.labels,
      axisLabel: { 
        color: '#64748b',
        interval: 0,
        rotate: 45
      }
    },
    yAxis: {
      type: 'value',
      axisLabel: { color: '#64748b' },
      splitLine: { lineStyle: { color: '#f1f5f9' } }
    },
    series: [{
      data: dash.charts.campaign.values,
      type: 'bar',
      itemStyle: {
        color: {
          type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
          colorStops: [
            { offset: 0, color: '#8b5cf6' },
            { offset: 1, color: '#6366f1' }
          ]
        }
      },
      barWidth: '60%'
    }]
  };
  const metricCards = [
    {
      title: "Total Fans",
      value: dash.metrics.totalFans.toLocaleString(),
      icon: Users,
      change: "+12.5%",
      changeType: "positive",
      color: "blue"
    },
    {
      title: "Total Guests",
      value: dash.metrics.totalGuests.toLocaleString(),
      icon: TrendingUp,
      change: "+8.2%",
      changeType: "positive",
      color: "emerald"
    },
    {
      title: "Campaigns Sent",
      value: dash.metrics.campaignsSent.toLocaleString(),
      icon: Mail,
      change: "+15.3%",
      changeType: "positive",
      color: "purple"
    },
    {
      title: "Conversion Rate",
      value: `${dash.metrics.conversionRate.toFixed(1)}%`,
      icon: Target,
      change: "+2.1%",
      changeType: "positive",
      color: "orange"
    }
  ];

  const colorClasses = {
    blue: {
      bg: "bg-blue-50 dark:bg-blue-500/10",
      icon: "text-blue-600 dark:text-blue-400",
      accent: "bg-blue-500"
    },
    emerald: {
      bg: "bg-emerald-50 dark:bg-emerald-500/10",
      icon: "text-emerald-600 dark:text-emerald-400",
      accent: "bg-emerald-500"
    },
    purple: {
      bg: "bg-purple-50 dark:bg-purple-500/10",
      icon: "text-purple-600 dark:text-purple-400",
      accent: "bg-purple-500"
    },
    orange: {
      bg: "bg-orange-50 dark:bg-orange-500/10",
      icon: "text-orange-600 dark:text-orange-400",
      accent: "bg-orange-500"
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-black p-6">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Header Section */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              {greeting}, {profile?.full_name || "Artist"}
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Here's what's happening with your audience today
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={exportDashboardCsv} disabled={loading} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-60">
              Export Data
            </button>
          </div>
        </div>

        {/* Metrics Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {metricCards.map((metric, index) => {
            const colors = colorClasses[metric.color];
            const Icon = metric.icon;
            
            return (
              <div
                key={metric.title}
                className="bg-white dark:bg-white/5 border-gray-200 dark:border-white/10 hover:border-white/20 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-md transition-all duration-200 group"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className={`w-12 h-12 rounded-lg ${colors.bg} flex items-center justify-center group-hover:scale-110 transition-transform duration-200`}>
                    <Icon className={`w-6 h-6 ${colors.icon}`} />
                  </div>
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                    {metric.value}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {metric.title}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Fan Growth Chart */}
          <div className="lg:col-span-2 rounded-xl p-6 shadow-sm bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 hover:border-white/20">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Audience Growth
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Monthly fan acquisition over time
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Activity className="w-5 h-5 text-gray-400" />
              </div>
            </div>
            
            <ReactECharts option={fanGrowthOption} style={{ height: '300px' }} />
            {loading && <p className="text-sm text-slate-500 dark:text-slate-400 mt-2">Loading…</p>}
          </div>

          {/* Top Performing Links */}
          <div className=" rounded-xl p-6 shadow-sm bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 hover:border-white/20">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Top Links
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Most clicked content
                </p>
              </div>
              <a href="/smart-links">
              <button className="text-blue-600 hover:text-blue-700 dark:text-blue-400">
                <ArrowRight className="w-5 h-5" />
              </button>
              </a>
            </div>
            
            <div className="space-y-4">
              {dash.topLinks.map((link, index) => (
                <div key={index} className="group cursor-pointer">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center text-white text-sm font-bold">
                        {index + 1}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                          {link.name}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {link.clicks.toLocaleString()} clicks
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="ml-11">
                    <div className="w-full bg-gray-100 dark:bg-gray-700 rounded-full h-2">
                      <div
                        className="bg-gradient-to-r from-blue-500 to-blue-600 h-2 rounded-full transition-all duration-500"
                        style={{ width: `${link.percentage}%` }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Campaign Performance */}
        <div className="rounded-xl p-6 shadow-sm bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 hover:border-white/20">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Campaign Performance
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Engagement rates by channel
              </p>
            </div>
            <BarChart3 className="w-5 h-5 text-gray-400" />
          </div>
          
          <ReactECharts option={campaignOption} style={{ height: '300px' }} />
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-4 text-center">
            Engagement rates by campaign type (%)
          </p>
        </div>

        {/* Quick Actions */}
        <div className="rounded-xl p-6 shadow-sm bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 hover:border-white/20">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            Quick Actions
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: Mail, label: "Create Campaign", color: "blue", href: "/create-campaign" },
              { icon: Users, label: "Manage Fans", color: "emerald", href: "/fans" },
              { icon: BarChart3, label: "Manage Guests", color: "purple", href: "/guests" },
              { icon: Zap, label: "Your Smart Links", color: "orange" , href: "/smart-links" }
            ].map((action, index) => {
              const colors = colorClasses[action.color];
              const Icon = action.icon;
              
              return (
                <>
                <a href={action.href}>
                <button
                  key={action.label}
                  className="flex items-center gap-3 p-4 text-left rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors group"
                >
                  <div className={`w-10 h-10 ${colors.bg} rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-200`}>
                    <Icon className={`w-5 h-5 ${colors.icon}`} />
                  </div>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {action.label}
                  </span>
                </button>
                </a>
                </>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;